import {
	ProviderDto,
	OrderEnum,
	ProviderInfoDto,
	DistrictFilter,
	PageDto,
	PageOptionsDto,
	SearchFilter,
	LanguageFilter,
} from '@families-link/shared';
import api from '.';

const providersApi = api.injectEndpoints({
	endpoints: (builder) => ({
		getProvidersList: builder.query<
			PageDto<ProviderDto>,
			PageOptionsDto & SearchFilter & { district?: string | null } & LanguageFilter & DistrictFilter
		>({
			query: ({ page, take, orderField, order, search, district, languages, districts }) => ({
				url: `providers/all/with-pagination`,
				params: { page, take, orderField, order, search, district, languages, districts },
			}),
			providesTags: ['Provider'],
		}),

		getAllProviders: builder.query<
			ProviderDto[],
			{
				languages?: string;
				sortBy?: string;
				sortOrder?: OrderEnum.ASC | OrderEnum.DESC;
				searchQuery?: string;
				districts?: string;
			}
		>({
			query: ({ languages, sortBy, sortOrder, searchQuery, districts }) => ({
				url: 'providers',
				params: { languages, sortBy, sortOrder, searchQuery, districts },
			}),
			providesTags: ['Provider', 'Engagement'],
		}),

		getAllProvidersByDistrictId: builder.query<
			ProviderDto[],
			{
				languages?: string;
				districtId?: string | null;
				sortBy?: string;
				sortOrder?: OrderEnum.ASC | OrderEnum.DESC;
				searchQuery?: string;
			}
		>({
			query: ({ languages, districtId, sortBy, sortOrder, searchQuery }) => ({
				url: 'providers/by/district',
				params: { languages, districtId, sortBy, sortOrder, searchQuery },
			}),
			providesTags: ['Provider', 'Engagement'],
		}),

		generateProviderPhotoUrl: builder.mutation<{ url: string }, FormData>({
			query: (data) => ({
				url: `providers/photo-url`,
				method: 'POST',
				body: data,
			}),
		}),

		createProvider: builder.mutation<ProviderDto, ProviderInfoDto>({
			query: (formData) => ({
				url: 'providers',
				method: 'POST',
				body: formData,
			}),
			invalidatesTags: ['Provider'],
		}),

		updateProvider: builder.mutation<ProviderInfoDto, { id: string; updateData: ProviderDto }>({
			query: ({ id, updateData }) => ({
				url: `providers/${id}`,
				method: 'PUT',
				body: updateData,
			}),
			invalidatesTags: ['Provider'],
		}),

		deleteProvider: builder.mutation<void, string>({
			query: (id) => ({
				url: `providers/${id}`,
				method: 'DELETE',
			}),
			invalidatesTags: ['Provider'],
		}),

		getProviderById: builder.query<ProviderDto, string | null | undefined>({
			query: (id) => ({
				url: `providers/${id}`,
				method: 'GET',
			}),
			providesTags: ['Provider'],
		}),
		importProviders: builder.mutation<
			{ importedCount: number; success: any[]; errors: any[]; warnings: any[] },
			{ formData: FormData; districtId?: string | null }
		>({
			query: ({ formData, districtId }) => ({
				url: `import/providers`,
				method: 'POST',
				body: formData,
				params: { district: districtId },
			}),
			invalidatesTags: ['Provider'],
		}),
		getProviderTemplateUrl: builder.query<{ url: string }, void>({
			query: () => ({
				url: 'import/providers/template-url',
				method: 'GET',
			}),
		}),
		createProviderArray: builder.mutation<{ success: any[]; failed: any[] }, any>({
			query: (data) => ({
				url: `providers/array`,
				method: 'POST',
				body: data,
			}),
			invalidatesTags: ['Provider'],
		}),
		getProviderFromLinguistLink: builder.query<ProviderDto, { id?: string | null }>({
			query: ({ id }) => ({
				url: `linguist-link/provider/${id}`,
			}),
		}),
	}),
	overrideExisting: false,
});

export default providersApi;

export const {
	useGetAllProvidersQuery,
	useGetAllProvidersByDistrictIdQuery,
	useCreateProviderMutation,
	useUpdateProviderMutation,
	useDeleteProviderMutation,
	useGetProviderByIdQuery,
	useImportProvidersMutation,
	useGetProviderTemplateUrlQuery,
	useGenerateProviderPhotoUrlMutation,
	useGetProvidersListQuery,
	useCreateProviderArrayMutation,
	useGetProviderFromLinguistLinkQuery,
} = providersApi;
